import React, {useCallback, useEffect, useRef, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {FarewiseIcon} from "../assets/svgComponents/farewiseIcon";
import {useTranslation} from "../i18n/LanguageProvider";
import {SharedShortlist, ShortlistError, ShortlistService} from "../services/ShortlistService";
import {useShortlistStore} from "../store/useShortlistStore";
import ShortlistFlight from "../components/shortlist/ShortlistFlight";

export default function SharedShortlistPage() {
  const {shortlistId = ""} = useParams();
  const {locale, t} = useTranslation();
  const [shared, setShared] = useState<SharedShortlist>();
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [working, setWorking] = useState(false);
  const inFlight = useRef(false);
  const generation = useRef({value: 0});
  const acceptShared = useShortlistStore(state => state.acceptShared);
  const setOpen = useShortlistStore(state => state.setOpen);

  const accept = useCallback((value: SharedShortlist) => {
    setShared(value); setError(null); setUnavailable(false);
    if (value.can_manage) acceptShared(value);
  }, [acceptShared]);

  const fail = useCallback((reason: unknown, message: string) => {
    if (reason instanceof ShortlistError && reason.status === 404) {
      setUnavailable(true); setShared(undefined);
    } else setError(message);
  }, []);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    const current = generation.current.value;
    inFlight.current = true;
    try {
      const result = await ShortlistService.get(shortlistId);
      if (current === generation.current.value) accept(result);
    } catch (reason) { if (current === generation.current.value) fail(reason, "shortlist.loadError"); }
    finally { if (current === generation.current.value) inFlight.current = false; }
  }, [shortlistId, accept, fail]);

  useEffect(() => {
    setShared(undefined); setUnavailable(false); setError(null);
    setWorking(false);
    const lifecycle = generation.current;
    inFlight.current = false;
    void load();
    const interval = window.setInterval(() => {if (!document.hidden) void load();}, 20000);
    const focus = () => void load();
    window.addEventListener("focus", focus);
    return () => {lifecycle.value++; clearInterval(interval); window.removeEventListener("focus", focus);};
  }, [load]);

  useEffect(() => {
    if (!shared) return;
    const remaining = shared.expires_at * 1000 - Date.now();
    if (remaining > 2147483647) return;
    const timeout = window.setTimeout(() => {setUnavailable(true); setShared(undefined);}, Math.max(0, remaining));
    return () => clearTimeout(timeout);
  }, [shared]);

  const change = async (action: () => Promise<SharedShortlist>, message: string) => {
    if (inFlight.current) return;
    const current = generation.current.value;
    inFlight.current = true; setWorking(true); setError(null);
    try { const value = await action(); if (current === generation.current.value) accept(value); }
    catch (reason) { if (current === generation.current.value) fail(reason, message); }
    finally { if (current === generation.current.value) {inFlight.current = false; setWorking(false);} }
  };

  return <main className="min-h-screen bg-[#F8F6FF] px-4 pb-16 pt-7 text-[#26213B] sm:px-8">
    <div className="mx-auto max-w-4xl">
      <header className="mb-10 flex flex-wrap items-center justify-between gap-4">
        <Link to="/" aria-label={t("home.aria")}><FarewiseIcon/></Link>
        <Link to="/" className="text-sm font-bold text-[#712FFF]">{t("share.searchAgain")}</Link>
      </header>
      {unavailable ? <section className="py-16 text-center">
        <h1 className="text-3xl font-bold">{t("shortlist.unavailable")}</h1><p className="mt-4 text-[#6A6480]">{t("shortlist.unavailableHint")}</p>
      </section> : <>
        <h1 className="text-4xl font-bold sm:text-5xl">{t("shortlist.publicTitle")}</h1>
        <p className="mt-4 max-w-2xl text-[#6A6480]">{t("shortlist.publicHint")}</p>
        {error && <div role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">{t(error)}
          <button type="button" onClick={() => void load()} className="ml-3 min-h-[44px] font-bold underline">{t("shortlist.retry")}</button></div>}
        {!shared && !error && <p role="status" className="py-12 text-[#712FFF]">{t("shortlist.loading")}</p>}
        {shared && <>
          <div className="my-6 flex flex-wrap items-center justify-between gap-3">
            <p role="status" className="font-bold text-[#712FFF]">{t("shortlist.totalVotes", {count: shared.total_votes})}</p>
            <button type="button" disabled={working} onClick={() => void load()}
              className="min-h-[44px] rounded-full border border-[#DED5F8] bg-white px-4 text-sm font-semibold text-[#712FFF] disabled:opacity-50">{t("shortlist.refresh")}</button>
          </div>
          {shared.can_manage && <div className="mb-5 rounded-xl bg-[#EDE5FF] p-4 text-sm text-[#5D339B]">
            <p>{t("shortlist.manage")}</p><button type="button" onClick={() => setOpen(true)} className="mt-1 min-h-[44px] font-bold underline">{t("shortlist.title")}</button>
          </div>}
          {shared.flights.length === 0 && <p className="py-10 text-center text-[#6A6480]">{t("shortlist.sharedEmpty")}</p>}
          <div className="grid gap-5 md:grid-cols-2">{shared.flights.map(entry => {
            const selected = shared.my_vote === entry.id;
            const popular = shared.leaders.includes(entry.id);
            return <article key={entry.id} className={`rounded-3xl border-2 bg-white p-5 shadow-sm sm:p-6 ${popular ? "border-[#A77CF5]" : "border-white"}`}>
              {popular && <p className="mb-3 w-fit rounded-full bg-[#EDE5FF] px-3 py-1 text-xs font-bold text-[#712FFF]">★ {t("shortlist.popular")}</p>}
              <ShortlistFlight flight={entry.flight}/>
              <div className="mt-5 flex items-center justify-between gap-3"><span className="text-sm font-bold text-[#6A6480]">{t("shortlist.votes", {count: entry.votes})}</span>
                <button type="button" disabled={working || selected} aria-pressed={selected}
                  onClick={() => void change(() => ShortlistService.vote(shortlistId, entry.id), "shortlist.voteError")}
                  className={`min-h-[44px] rounded-full px-4 text-sm font-bold disabled:opacity-70 ${selected ? "bg-[#EDE5FF] text-[#712FFF]" : "bg-[#712FFF] text-white"}`}>
                  {t(selected ? "shortlist.yourVote" : "shortlist.vote")}</button>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#F1EDF8]" aria-hidden="true"><div className="h-full rounded-full bg-[#A77CF5] transition-all" style={{width: `${shared.total_votes ? entry.votes / shared.total_votes * 100 : 0}%`}}/></div>
              {shared.can_manage && <button type="button" disabled={working} onClick={() => void change(
                () => ShortlistService.update(shortlistId, shared.flights.filter(item => item.id !== entry.id).map(item => item.flight)), "shortlist.saveError")}
                className="mt-3 min-h-[44px] text-xs text-[#6A6480] underline disabled:opacity-50">{t("shortlist.remove")}</button>}
            </article>;
          })}</div>
          <footer className="mt-8 space-y-2 text-xs leading-relaxed text-[#6A6480]">
            <p>{t("shortlist.expiry", {date: new Date(shared.expires_at * 1000).toLocaleDateString(locale)})}</p>
            <p>{t("shortlist.priceHint")}</p>
          </footer>
        </>}
      </>}
    </div>
  </main>;
}
